import React, { useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import { API_KEY } from './constants.js';
import { LocationCard } from "./LocationCard";
import { APIProvider, Map, useMap, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

export function ConcertMap(props) {
  let title = props.title ? props.title : 'Map';
  return (
    <div className="Map">
      <h2>{title}</h2>
      <APIProvider apiKey={API_KEY}>
        <Map
          style={{width: '100vw', height: '75vh'}}
          defaultCenter={{lat: 53.736129, lng: -1.988229}}
          defaultZoom={6}
          gestureHandling='greedy'
          disableDefaultUI
          mapId="DEMO_MAP_ID"
        >
          <ClusteredMarkers locations={props.markers}/>
        </Map>
      </APIProvider>
    </div>
  );
};

export const ClusteredMarkers = (props) => {
  const [locations, setLocations] = useState(props.locations);
  const [markers, setMarkers] = useState({});
  const [bounds, setBounds] = useState(new window.google.maps.LatLngBounds());
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  useEffect(() => { setSelectedLocationId(null); setLocations(props.locations); }, [props])

  useEffect(() => {
    Object.values(locations).map(function(location) {
      return bounds.extend(new window.google.maps.LatLng(location.lat, location.lng));
    });
    map.fitBounds(bounds);
    var zoom = map.getZoom();
    map.setZoom(zoom > 15 ? 15 : zoom);
  }, [locations, bounds]);

  const selectedLocation = useMemo(
    () =>
      locations && selectedLocationId
        ? locations[selectedLocationId]
        : null,
    [locations, selectedLocationId]
  );

  const map = useMap();

  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({map});
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    setBounds(new window.google.maps.LatLngBounds());
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers, locations]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers(markers => {
      if ((marker && markers[key]) || (!marker && !markers[key])) {
        return markers;
      }

      if (marker) {
        return {...markers, [key]: marker};
      } else {
        const {[key]: _, ...newMarkers} = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedLocationId(null);
  }, []);

  const handleMarkerClick = useCallback((location) => {
    setSelectedLocationId(location.location_id);
  }, []);

  return (
    <>
      {Object.values(locations).map(location => (
        <LocationMarker
          key={location.location_id}
          location={location}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}

      {selectedLocationId && (
        <InfoWindow
          anchor={markers[selectedLocationId]}
          onCloseClick={handleInfoWindowClose}>
          <LocationCard location={selectedLocation} key={selectedLocationId}/>
        </InfoWindow>
      )}
    </>
  );
};

export const LocationMarker = (props) => {
  const {location, onClick, setMarkerRef} = props;

  const handleClick = useCallback(() => onClick(location), [onClick, location]);
  const ref = useCallback(
    (marker) =>
      setMarkerRef(marker, location.location_id),
    [setMarkerRef, location.location_id]
  );

  return (
    <AdvancedMarker position={{lng: location.lng, lat: location.lat}} ref={ref} onClick={handleClick}/>
  );
};